import React, { useState, useEffect, useRef } from 'react'
import styled, { keyframes, useTheme } from 'styled-components'
import { X } from 'lucide-react'
import { swallerIcons, getSwallerColor } from './icons'

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const ToastWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 320px;
  max-width: 400px;
  padding: 1rem 1.25rem;
  background: ${(props) => props.theme.colors.background.primary};
  border-radius: 12px;
  border-left: 4px solid ${(props) => props.$color};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${(props) => (props.$isExiting ? fadeOut : slideIn)} 0.3s ease-out;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateX(-4px);
  }

  @media (max-width: 768px) {
    min-width: 280px;
    max-width: 90vw;
  }
`

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color};
`

const Message = styled.div`
  flex: 1;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.text.secondary};
  line-height: 1.4;
  word-break: break-word;
`

const CloseButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.colors.text.secondary};
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`

export const SwallerToast = ({ id, type, text, duration, onDismiss }) => {
  const theme = useTheme()
  const [isExiting, setIsExiting] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const timerRef = useRef(null)
  const startTimeRef = useRef(Date.now())
  const remainingTimeRef = useRef(duration)

  const color = getSwallerColor(type, theme)
  const Icon = swallerIcons[type]

  const handleDismiss = () => {
    setIsExiting(true)
    setTimeout(() => {
      onDismiss(id)
    }, 200) // Match fadeOut animation duration
  }

  const startTimer = () => {
    if (duration <= 0) return

    startTimeRef.current = Date.now()
    timerRef.current = setTimeout(() => {
      handleDismiss()
    }, remainingTimeRef.current)
  }

  const pauseTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      const elapsed = Date.now() - startTimeRef.current
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed)
    }
  }

  useEffect(() => {
    if (isPaused) {
      pauseTimer()
    } else {
      startTimer()
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused])

  return (
    <ToastWrapper
      $color={color}
      $isExiting={isExiting}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onClick={handleDismiss}
    >
      <IconWrapper $color={color}>
        {Icon && <Icon size={24} strokeWidth={2} />}
      </IconWrapper>
      <Message>{text}</Message>
      <CloseButton onClick={(e) => {
        e.stopPropagation()
        handleDismiss()
      }}>
        <X size={18} />
      </CloseButton>
    </ToastWrapper>
  )
}
